import {
    CheckBoxWithLabelWidget, CompactInlineAlertWidget, InlineAlertType,
    RadioButtonWithLabelGroupWidget,
    TextFieldWidget,
    TextInputType
} from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import {
    samlProviderAlertText,
    samlProviderGroupSettings
} from '~src/settings/siteAuthSettings/styles/SAMLAuthenticationProviderStyle';

import { Messages } from '../../../codegen/Localize';
import { IOIDCAuthenticationProvider } from '../services/SiteAuthenticationSettingsService';
import {
    externalProviderNameClaimInput,
    externalProviderSectionPadding,
    externalProviderTextDescriptionStyle,
    externalProviderTextInput,
    externalProviderTextSubTitleStyle,
    externalProviderTextTitleStyle
} from '../styles/ExternalAuthenticationProviderStyle';
import { isUrlValid } from '../utils/SiteAuthenticationSettingsUtils';


export interface OIDCProviderProps {
    provider: IOIDCAuthenticationProvider;
    showTestConfiguration: boolean;
    hasUpdated: boolean;
    updateClientId?: (clientId: string) => void;
    updateClientSecret?: (clientSecret: string) => void;
    updateConfigurationUrl?: (url: string) => void;
    updateEmbeddedOption?: (allow: boolean) => void;
    updateUseFullName?: (allow: boolean) => void;
    updateFirstNameMapping?: (name: string) => void;
    updateLastNameMapping?: (name: string) => void;
    updateFullNameMapping?: (name: string) => void;
    updateEmailMapping?: (email: string) => void;
    updateSingleLougout?: (enable: boolean) => void;
    updateEmailAddressMapping?: (email: string) => void;
}

export interface OIDCProviderState {
    embeddedOptionRadioButtonIndex: number;
    fullNameRadioButtonIndex: number;
}

// If client secret was stored, backend returns this value instead of the real secret value.
export const omittedClientSecret = '<omit>';

export class OIDCAuthenticationProvider extends React.Component<OIDCProviderProps, OIDCProviderState> {
    private loginUrl: string;
    private oidcLoginRedirectUrl: string;

    constructor(props: OIDCProviderProps) {
        super(props);
        this.state = {
            embeddedOptionRadioButtonIndex: this.props.provider.allowEmbeddedAuthentication ? 1 : 0,
            fullNameRadioButtonIndex: this.props.provider.useFullName ? 0 : 1
        };

        const url = new URL(this.props.provider.testLoginUrl);
        const baseUrl = `${url.protocol}//${url.hostname}`;
        this.oidcLoginRedirectUrl = baseUrl + '/public/oidc/login';
        this.loginUrl = baseUrl + '/public/login';
    }

    private renderStep1(): JSX.Element {
        return (
            <div style={externalProviderSectionPadding}>
                <div style={externalProviderTextTitleStyle}>
                    {Messages.siteOIDCConfigurationStep1()}
                </div>
                <div style={externalProviderTextDescriptionStyle}>
                    {Messages.siteOIDCConfigurationStep1Description()}
                    <p></p>

                    <TextFieldWidget
                        onBlur={(evt) => this.props.updateClientId(evt.target.value) }
                        testId='site-oidc-config-client-id'
                        label={Messages.siteOIDCConfigurationClientId()}
                        kind='line'
                        style={externalProviderTextInput}
                        defaultValue={this.props.provider.clientId}>
                    </TextFieldWidget>
                    <p></p>

                    <TextFieldWidget
                        onBlur={(evt) => this.props.updateClientSecret(evt.target.value)}
                        testId='site-oidc-config-client-secret'
                        label={Messages.siteOIDCConfigurationClientSecret()}
                        kind='line'
                        style={externalProviderTextInput}
                        placeholder={this.props.provider.clientSecret === omittedClientSecret ? '**********' : null}
                        type={TextInputType.Password}>
                    </TextFieldWidget>
                    <p></p>

                    <TextFieldWidget
                        onBlur={(evt) => this.props.updateConfigurationUrl(evt.target.value)}
                        testId='site-oidc-config-config-url'
                        label={Messages.siteOIDCConfigurationConfigurationUrl()}
                        kind='line'
                        style={externalProviderTextInput}
                        defaultValue={this.props.provider.configurationUrl}>
                    </TextFieldWidget>
                    {!isUrlValid(this.props.provider.configurationUrl) ?
                      <div style={samlProviderGroupSettings}>
                        <CompactInlineAlertWidget type={InlineAlertType.Error} testId="" style={samlProviderAlertText}>
                          <div>{Messages.invalidUrl()}</div>
                        </CompactInlineAlertWidget>
                      </div>
                      : null}
                </div>
            </div>
        );
    }

    private renderStep2(): JSX.Element {
        return (
            <div style={externalProviderSectionPadding}>
                <div style={externalProviderTextTitleStyle}>
                    {Messages.siteOIDCConfigurationStep2()}
                </div>
                <div style={externalProviderTextDescriptionStyle}>
                    {Messages.siteOIDCConfigurationStep2Description()}
                    <p></p>

                    <TextFieldWidget
                        testId='site-oidc-config-redirect-url'
                        label={Messages.siteOIDCConfigurationRedirectUrl()}
                        kind='line'
                        style={externalProviderTextInput}
                        defaultValue={this.oidcLoginRedirectUrl}
                        readOnly={true}>
                    </TextFieldWidget>
                </div>
            </div>
        );
    }

    private renderStep3(): JSX.Element {
        const canDecoupleEmail = FeatureFlags.usernameEmailDecouplingEnabled;
        return (
            <div style={externalProviderSectionPadding}>
                <div style={externalProviderTextTitleStyle}>
                    {Messages.siteOIDCConfigurationStep3()}
                </div>
                <div style={externalProviderTextDescriptionStyle}>
                    {Messages.siteOIDCConfigurationStep3Description()}
                    <p></p>

                    <div style={externalProviderTextSubTitleStyle}>
                        {Messages.username()}
                    </div>

                    {Messages.siteOIDCConfigurationUsernameClaimDescription()}
                    <p></p>

                    <TextFieldWidget
                        onBlur={(evt) => this.props.updateEmailMapping(evt.target.value)}
                        testId='site-oidc-config-username-claim'
                        label={Messages.username()}
                        kind='line'
                        style={externalProviderTextInput}
                        defaultValue={this.props.provider.emailMapping ?? null}
                        placeholder='email'>
                    </TextFieldWidget>
                    <p></p>
                    { (canDecoupleEmail) &&
                    <div>
                        <div style={externalProviderTextSubTitleStyle}>
                            {Messages.emailAddress()}
                        </div>

                        {Messages.siteAuthenticationOIDCEmailAddressMappingDesc()}
                        <p></p>

                        <TextFieldWidget
                            onBlur={(evt) => this.props.updateEmailAddressMapping(evt.target.value)}
                            testId='site-oidc-config-email-claim'
                            label={Messages.emailAddress()}
                            kind='line'
                            style={externalProviderTextInput}
                            defaultValue={this.props.provider.emailAddressMapping ?? ''}
                            placeholder=''>
                        </TextFieldWidget>
                    </div>}
                    <p></p>

                    <div style={externalProviderTextSubTitleStyle}>
                        {Messages.siteOIDCConfigurationNameClaim()}
                    </div>

                    {Messages.siteOIDCConfigurationNameClaimDescription()}
                    <p></p>

                    {this.renderNameClaimRadioButtons()}
                </div>
            </div>
        );
    }

    private renderNameClaimRadioButtons(): JSX.Element {
        const nameClaimRadioButtons = [
          {
            label: Messages.siteOIDCConfigurationNameClaim(),
            testId: 'site-oidc-config-name-claim',
            detail: this.state.fullNameRadioButtonIndex === 0 ? (
                <div>
                    <p></p>
                    <TextFieldWidget
                        onBlur={(evt) => this.props.updateFullNameMapping(evt.target.value)}
                        testId='site-oidc-config-full-name-claim'
                        label={Messages.siteOIDCConfigurationNameClaimLabel()}
                        kind='line'
                        style={externalProviderNameClaimInput}
                        defaultValue={this.props.provider.fullNameMapping ?? null}
                        placeholder='name'>
                    </TextFieldWidget>
                    <p></p>
                </div>
                ) : null,
            value: true
          },
          {
            label: Messages.siteOIDCConfigurationGivenAndFamilyNameClaims(),
            testId: 'site-oidc-config-first-last-name-claim',
            detail: this.state.fullNameRadioButtonIndex === 1 ? (
                <div>
                    <p></p>
                    <TextFieldWidget
                        onBlur={(evt) => this.props.updateFirstNameMapping(evt.target.value)}
                        testId='site-oidc-config-first-name-claim'
                        label={Messages.siteOIDCConfigurationGivenNameLabel()}
                        kind='line'
                        style={externalProviderNameClaimInput}
                        defaultValue={this.props.provider.firstNameMapping ?? null}
                        placeholder='given_name'>
                    </TextFieldWidget>
                    <p></p>
                    <TextFieldWidget
                        onBlur={(evt) => this.props.updateLastNameMapping(evt.target.value)}
                        testId='site-oidc-config-last-name-claim'
                        label={Messages.siteOIDCConfigurationFamilyNameLabel()}
                        kind='line'
                        style={externalProviderNameClaimInput}
                        defaultValue={this.props.provider.lastNameMapping ?? null}
                        placeholder='family_name'>
                    </TextFieldWidget>
                </div>
                ) : null,
            value: false
          }
        ];

        const handleChange = (useFullName: boolean) => {
            this.props.updateUseFullName(useFullName);
            this.setState({fullNameRadioButtonIndex: useFullName ? 0 : 1});
        };

        return (
          <RadioButtonWithLabelGroupWidget
            aria-label={Messages.siteOIDCConfigurationNameClaimDescription()}
            buttonList={nameClaimRadioButtons}
            checkedIndex={this.state.fullNameRadioButtonIndex}
            handleChange={handleChange}
            removeLeftMargin={true}
          />
        );
    }

    private renderStep4(): JSX.Element {
        return (
            <div style={externalProviderSectionPadding}>
                <div style={externalProviderTextTitleStyle}>
                    {Messages.siteOIDCConfigurationStep4()}
                </div>
                <div style={externalProviderTextDescriptionStyle}>
                    {Messages.siteOIDCConfigurationStep4Description()}
                    <p></p>

                    <CheckBoxWithLabelWidget
                        checked={this.props.provider.isLogoutSupported}
                        handleChange={this.props.updateSingleLougout}
                        testId={'site-oidc-config-enable-slo'}
                        label={Messages.siteOIDCConfigurationSingleLogoutLabel()}
                        removeLeftMargin={true}
                        disabled={false} />

                    {this.props.provider.isLogoutSupported ?
                        <div style={externalProviderTextDescriptionStyle}>
                            <p></p>
                            {Messages.siteOIDCConfigurationPostLogoutUrlDescription()}
                            <p></p>

                            <TextFieldWidget
                                testId='site-oidc-config-post-logout-url'
                                label={Messages.siteOIDCConfigurationPostLogoutUrlLabel()}
                                kind='line'
                                style={externalProviderTextInput}
                                defaultValue={this.loginUrl}
                                readOnly={true}>
                            </TextFieldWidget>
                        </div>
                        : null}
                </div>
            </div>
        );
    }

    private renderStep5(): JSX.Element {
        const embeddedOptionsButtons = [
            {
                label: Messages.siteAuthenticationEmbeddingDenied(),
                testId: 'site-oidc-config-embedded-viz-popup-button',
                value: false
            },
            {
                label: Messages.siteAuthenticationEmbeddingAllowed(),
                testId: 'site-oidc-config-embedded-viz-iframe-button',
                value: true
            }
        ];

        const handleChange = (allow: boolean) => {
            this.props.updateEmbeddedOption(allow);
            this.setState({embeddedOptionRadioButtonIndex: allow? 1 : 0});
        };

        return (
            <div style={externalProviderSectionPadding}>
                <div style={externalProviderTextTitleStyle}>
                    {Messages.siteOIDCConfigurationStep5()}
                </div>
                <div style={externalProviderTextDescriptionStyle}>
                    {Messages.siteOIDCConfigurationStep5Description()}
                    <p></p>

                    <RadioButtonWithLabelGroupWidget
                        aria-label={Messages.siteOIDCConfigurationStep5Description()}
                        className={'tb-dialog-authentication-option'}
                        testId="embedded-option-radio-buttons"
                        buttonList={embeddedOptionsButtons}
                        checkedIndex={this.state.embeddedOptionRadioButtonIndex}
                        handleChange={handleChange}
                        removeLeftMargin={true}
                    />
                </div>
            </div>
        );
    }

    public render(): JSX.Element {
        return (
            <div>
                <div style={externalProviderTextDescriptionStyle}>
                    {Messages.siteAuthenticationTypeOIDCDescription2()}
                </div>
                {this.renderStep1()}
                {this.renderStep2()}
                {this.renderStep3()}
                {this.renderStep4()}
                {this.renderStep5()}
            </div>
        );
    }
}
